import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { api } from '../../lib/api'
import { useAuthStore } from '../../stores/auth'
import { Card } from '../../components/ui/Card'
import { Field } from '../../components/ui/Field'
import { Button } from '../../components/ui/Button'
import { PasswordField } from '../../components/ui/PasswordField'

interface SettingsResponse {
  settings: {
    shop: string
    apiVersion: string
    tokenSet: boolean
    active: boolean
    lastSyncAt: string | null
    lastSyncCount: number | null
    lastSyncOrderId: string | null
    updatedAt: string
  } | null
  syncInProgress: boolean
  lastSyncResult: {
    totalProcessed?: number
    newOrders?: number
    updatedOrders?: number
    durationMs?: number
    error?: string
  } | null
}

export function SetariShopifyPage() {
  const queryClient = useQueryClient()
  const permissions = useAuthStore((s) => s.permissions)
  const canWrite = permissions.includes('marketing.shopify:write')

  const [shop, setShop] = useState('')
  const [accessToken, setAccessToken] = useState('')
  const [apiSecret, setApiSecret] = useState('')

  const query = useQuery<SettingsResponse>({
    queryKey: ['shopify-settings'],
    queryFn: async () => (await api.get<SettingsResponse>('/shopify/settings')).data,
    refetchInterval: (q) => (q.state.data?.syncInProgress ? 3_000 : false),
  })

  const settings = query.data?.settings
  const tokenSet = settings?.tokenSet
  const lastSync = settings?.lastSyncAt ? new Date(settings.lastSyncAt) : null

  const saveMutation = useMutation({
    mutationFn: async () =>
      (
        await api.put('/shopify/settings', {
          shop: shop.trim(),
          accessToken: accessToken.trim(),
          apiSecret: apiSecret.trim() || null,
        })
      ).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shopify-settings'] })
      toast.success('Setări salvate')
      setAccessToken('')
      setApiSecret('')
    },
    onError: showError,
  })

  const testMutation = useMutation({
    mutationFn: async () => {
      const body =
        shop.trim() && accessToken.trim()
          ? { shop: shop.trim(), accessToken: accessToken.trim() }
          : {}
      return (await api.post('/shopify/settings/test-connection', body)).data
    },
    onSuccess: (d: unknown) => {
      const r = d as {
        ok: boolean
        name?: string
        currency?: string
        plan?: string
        error?: { message?: string }
      }
      if (r.ok) {
        toast.success(`Conectat: ${r.name} (${r.currency} / ${r.plan})`)
      } else {
        toast.error(r.error?.message ?? 'Eroare')
      }
    },
    onError: showError,
  })

  const syncMutation = useMutation({
    mutationFn: async (full: boolean) => (await api.post('/shopify/sync', { full })).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shopify-settings'] })
      toast.success('Sincronizarea a pornit. Starea se actualizează automat.')
    },
    onError: showError,
  })

  return (
    <div className="space-y-4">
      <h1 className="text-xl font-semibold">Setări → Shopify</h1>

      <Card title="Conexiune">
        {settings && (
          <div className="mb-4 rounded border border-b4l-blue-light bg-b4l-blue-light/30 p-3 text-xs">
            <div>
              <strong>Shop curent:</strong> {settings.shop}
            </div>
            <div>
              <strong>Token configurat:</strong> {tokenSet ? '✓ da' : '✗ nu'}
            </div>
            <div>
              <strong>Versiune API:</strong> {settings.apiVersion}
            </div>
            <div>
              <strong>Ultima sincronizare:</strong>{' '}
              {lastSync
                ? `${lastSync.toLocaleString('en-GB')} (${settings.lastSyncCount ?? 0} ordere)`
                : 'niciodată'}
            </div>
          </div>
        )}

        <div className="space-y-3">
          <Field
            label="Shop URL"
            placeholder="ex: 268819-11.myshopify.com sau boost4life.myshopify.com"
            value={shop}
            onChange={(e) => setShop(e.target.value)}
            disabled={!canWrite}
          />
          <PasswordField
            label={
              tokenSet
                ? 'Access token nou (lasă gol pentru a păstra)'
                : 'Admin API access token (shpat_...)'
            }
            placeholder="shpat_..."
            value={accessToken}
            onChange={(e) => setAccessToken(e.target.value)}
            disabled={!canWrite}
          />
          <PasswordField
            label="API secret (opțional, pentru webhooks HMAC)"
            value={apiSecret}
            onChange={(e) => setApiSecret(e.target.value)}
            disabled={!canWrite}
          />

          <div className="flex flex-wrap gap-2">
            {canWrite && (
              <>
                <Button
                  onClick={() => saveMutation.mutate()}
                  loading={saveMutation.isPending}
                  disabled={!shop.trim() || !accessToken.trim()}
                >
                  Salvează
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => testMutation.mutate()}
                  loading={testMutation.isPending}
                  disabled={!tokenSet && (!shop.trim() || !accessToken.trim())}
                >
                  Test conexiune
                </Button>
              </>
            )}
          </div>
        </div>
      </Card>

      <Card
        title="Sincronizare comenzi"
        description="Trage istoric Shopify în baza locală. Idempotent: rulări multiple actualizează doar comenzile schimbate."
      >
        {query.data?.syncInProgress && (
          <div className="mb-3 rounded border border-b4l-amber-light bg-b4l-amber-light/30 p-3 text-xs">
            <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-b4l-amber-dark" />{' '}
            Sincronizare în desfășurare… Pagina se actualizează automat.
          </div>
        )}
        {query.data?.lastSyncResult && (
          <div
            className={`mb-3 rounded border p-3 text-xs ${
              'error' in query.data.lastSyncResult
                ? 'border-b4l-red-light bg-b4l-red-light/30'
                : 'border-b4l-green-light bg-b4l-green-light/30'
            }`}
          >
            {'error' in query.data.lastSyncResult ? (
              <span>Eroare: {query.data.lastSyncResult.error}</span>
            ) : (
              <span>
                Procesate: {query.data.lastSyncResult.totalProcessed ?? 0} (
                {query.data.lastSyncResult.newOrders ?? 0} noi,{' '}
                {query.data.lastSyncResult.updatedOrders ?? 0} actualizate) în{' '}
                {Math.round((query.data.lastSyncResult.durationMs ?? 0) / 1000)}s
              </span>
            )}
          </div>
        )}

        {canWrite && tokenSet && (
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={() => syncMutation.mutate(false)}
              loading={syncMutation.isPending}
              disabled={query.data?.syncInProgress}
            >
              Sincronizează modificările
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                if (
                  confirm(
                    'Sincronizarea completă recitește toate comenzile din magazin (zeci de mii) și poate dura 5-10 minute. Nu pierzi nimic din CRM. Continui?',
                  )
                )
                  syncMutation.mutate(true)
              }}
              loading={syncMutation.isPending}
              disabled={query.data?.syncInProgress}
            >
              Sincronizează tot
            </Button>
          </div>
        )}
        {!tokenSet && (
          <p className="text-sm text-b4l-text-muted">
            Salvează întâi tokenul, ca să poți sincroniza.
          </p>
        )}
      </Card>

      <Card description="Cum obții token: Shopify Admin → Settings → Apps and sales channels → Develop apps → Create app → Configure Admin API scopes (read_orders, read_products) → Install → Reveal admin API access token.">
        <p className="text-xs text-b4l-text-muted">
          <strong>Notă scope:</strong> tokenul folosit acum (cel din ExpertAccounts) are doar
          read_orders + read_products. Datele despre clienți și voucherele sunt extrase direct din
          comenzi (Shopify le include în payload). Pentru sync de clienți fără comenzi sau access la
          price_rules e necesar token cu scope-uri suplimentare (read_customers, read_price_rules).
        </p>
      </Card>
    </div>
  )
}

function showError(err: unknown) {
  const message =
    (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
      ?.message ?? 'Eroare'
  toast.error(message)
}
